import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { Badge, Button, Card, cn, EmptyState, Spinner, TextArea } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import type { IncidentDto } from '../../lib/types';
import { INCIDENT_LABEL } from '../safety/RideIncidents';

/** Administración: emergencias, "¿Todo bien?", reportes de seguridad (con audio) y objetos perdidos. */
export function IncidentsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<'OPEN' | 'RESOLVED'>('OPEN');
  const q = useQuery({ queryKey: ['incidents-admin', status], queryFn: () => api<IncidentDto[]>('/incidents', { query: { status } }), refetchInterval: 30_000 });
  const [notes, setNotes] = useState<Record<string, string>>({});

  const resolve = async (id: string) => {
    try {
      await api(`/incidents/${id}/resolve`, { method: 'PATCH', body: { resolution: notes[id]?.trim() || 'Revisado por la administración' } });
      toast('Marcado como resuelto', 'success');
      await qc.invalidateQueries({ queryKey: ['incidents-admin'] });
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  return (
    <Page title="Seguridad y reportes" back="/driver/more" onRefresh={() => q.refetch()}>
      <div className="flex flex-col gap-4">
        <div role="radiogroup" aria-label="Estado" className="grid grid-cols-2 gap-2">
          {([
            ['OPEN', 'Abiertos'],
            ['RESOLVED', 'Resueltos'],
          ] as const).map(([s, l]) => (
            <button key={s} role="radio" aria-checked={status === s} onClick={() => setStatus(s)} className={cn('tap h-11 rounded-2xl border font-semibold', status === s ? 'bg-brand-black text-white border-brand-black dark:bg-white dark:text-brand-black' : 'border-[var(--border)] bg-[var(--surface)]')}>
              {l}
            </button>
          ))}
        </div>
        {q.isLoading && <Spinner />}
        {q.data?.length === 0 && <EmptyState title={status === 'OPEN' ? 'Sin reportes abiertos' : 'Sin reportes resueltos'} />}
        {q.data?.map((i) => (
          <Card key={i.id} className={cn('p-4 flex flex-col gap-2 text-sm', i.type === 'SOS' && i.status === 'OPEN' && 'border-2 border-brand-red')}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold">{INCIDENT_LABEL[i.type]}</p>
              <Badge tone={i.type === 'SOS' ? 'danger' : i.status === 'OPEN' ? 'warning' : 'success'}>{i.status === 'OPEN' ? 'Abierto' : 'Resuelto'}</Badge>
            </div>
            {i.ride && (
              <p className="text-[var(--text-muted)]">
                <Link to={`/driver/rides/${i.ride.id}`} className="underline font-medium text-[var(--text)]">{i.ride.code}</Link> · Cliente: {i.ride.customer} · Conductor: {i.ride.driver}
              </p>
            )}
            {i.description && <p>{i.description}</p>}
            {i.reporterRole && <p className="text-xs text-[var(--text-muted)]">Reportado por: {i.reporterRole === 'CUSTOMER' ? 'el cliente' : i.reporterRole === 'DRIVER' ? 'el conductor' : 'la administración'}</p>}
            {i.location && (
              <a className="tap underline flex items-center gap-1 w-fit" href={`https://www.google.com/maps?q=${i.location.lat},${i.location.lng}`} target="_blank" rel="noreferrer">
                <MapPin className="size-4" aria-hidden /> Ver ubicación
              </a>
            )}
            {i.audioUrl && <audio controls src={i.audioUrl} className="w-full" />}
            <p className="text-xs text-[var(--text-muted)]">{fmtDateTime(i.createdAt)}</p>
            {i.resolution && <p className="rounded-xl bg-[var(--surface-2)] p-3">{i.resolution}</p>}
            {i.status === 'OPEN' && (
              <div className="flex flex-col gap-2">
                <TextArea label="Nota de cierre (opcional)" value={notes[i.id] ?? ''} onChange={(e) => setNotes({ ...notes, [i.id]: e.target.value })} maxLength={500} />
                <Button size="sm" onClick={() => void resolve(i.id)}>Marcar como resuelto</Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </Page>
  );
}
